/**
 * French (fr) translations.
 *
 * Same key shape as the Slovak source dictionary (`Dictionary` = `typeof sk`).
 * "Solandio" is an indeclinable brand name — never translated or inflected.
 *
 * DUAL-MODE COPY: like the Slovak source, this dictionary ships BOTH the warm
 * family-mode wording and the co-parenting `*Separated` variants. The bare keys
 * default to the family-mode (warm) copy. ZERO legal vocabulary in family mode —
 * `tribunal`, `garde alternée`, `passation`, `accord`, `50/50` appear ONLY in
 * the explicit `*Separated` strings.
 *
 * Do NOT change the `Dictionary` shape — only translate values.
 */
import { type Dictionary } from './sk';

export const fr: Dictionary = {
  appName: 'Solandio',
  tagline: 'Une zone ensoleillée pour vos enfants et votre famille.',

  language: {
    label: 'Langue',
    select: 'Choisir la langue',
  },

  common: {
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    cancel: 'Annuler',
    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    remove: 'Retirer',
    close: 'Fermer',
    loading: 'Chargement…',
    none: 'Aucun',
    optional: 'facultatif',
    back: 'Retour',
    today: 'Aujourd’hui',
    note: 'Note',
    required: 'Ce champ est obligatoire.',
    error: 'Une erreur s’est produite.',
    retry: 'Réessayer',
    confirmDelete: 'Voulez-vous vraiment supprimer ?',
    skipToContent: 'Aller au contenu',
  },

  auth: {
    signInTitle: 'Bienvenue sur Solandio',
    signInLead:
      'Un espace calme et clair où toute la famille reste organisée — calendrier, enfants, courses et dépenses.',
    signInMicrosoft: 'Se connecter ou s’inscrire',
    signInGoogle: 'Se connecter avec Google',
    signInMock: 'Se connecter (développement local)',
    signOut: 'Se déconnecter',
    signedInAs: 'Connecté(e) en tant que',
    loadingSession: 'Vérification de la connexion…',
    warmingUp:
      'Préparation de Solandio… au premier lancement après un certain temps, cela peut prendre quelques secondes.',
    devNotice:
      'Mode développement local — sans Azure. Les données sont stockées uniquement dans votre navigateur.',
  },

  nav: {
    home: 'Accueil',
    children: 'Enfants',
    contacts: 'Contacts',
    health: 'Santé',
    calendar: 'Calendrier',
    shopping: 'Courses',
    documents: 'Documents',
    messages: 'Mots',
    expenses: 'Dépenses',
    agreements: 'Accords',
    sharing: 'Famille',
    audit: 'Journal',
    settings: 'Paramètres',
    modules: 'Modules',
    menu: 'Menu',
  },

  settings: {
    title: 'Paramètres',
    lead: 'Gérez votre profil, votre langue et votre connexion.',
    profile: 'Profil',
    displayName: 'Nom affiché',
    displayNameHint: 'C’est le nom que voient les autres membres de la famille.',
    email: 'E-mail',
    emailHint: 'Votre e-mail ne peut pas être modifié — il est lié à votre connexion.',
    nameInvalid: 'Saisissez un nom (1 à 120 caractères).',
    saved: 'Enregistré.',
    preferences: 'Préférences',
    account: 'Compte',
    signOutHint: 'Se déconnecter de cet appareil.',
  },

  profileSetup: {
    title: 'Bienvenue sur Solandio !',
    lead: 'Avant de commencer, dites-nous comment vous appeler.',
    nameLabel: 'Comment devons-nous vous appeler ?',
    nameHint: 'Les autres membres de la famille verront ce nom. Vous pouvez le modifier à tout moment dans les Paramètres.',
    namePlaceholder: 'p. ex. Jeanne',
    nameInvalid: 'Veuillez saisir un nom (1 à 120 caractères).',
    submit: 'Continuer',
  },

  family: {
    switcher: 'Famille',
    create: 'Créer une famille',
    createTitle: 'Créez votre famille',
    createLead:
      'La famille est votre espace commun — pour le partenaire, les enfants et les grands-parents. C’est vous qui décidez qui voit quoi.',
    nameLabel: 'Nom de la famille',
    namePlaceholder: 'ex. Notre famille',
    empty: 'Vous n’avez encore aucune famille.',
  },

  home: {
    /** Greeting; {name} is the signed-in member's first name. */
    greetingMorning: 'Bonjour, {name}',
    greetingDay: 'Bonjour, {name}',
    greetingEvening: 'Bonsoir, {name}',
    /** Fallback greeting when no name is known yet. */
    greetingNeutral: 'Bienvenue à la maison',
    add: 'Ajouter',
    /** Section titles on the at-a-glance dashboard. */
    todayTitle: 'Aujourd’hui',
    childrenTitle: 'Enfants',
    shoppingTitle: 'Courses',
    expensesTitle: 'Dépenses du foyer',
    lastNoteTitle: 'Dernier mot',
    /** Small links/summaries under sections. */
    tomorrow: 'Demain',
    tomorrowEvents: 'Demain : {count} événements',
    viewAll: 'Tout afficher',
    nothingToday: 'Rien au calendrier aujourd’hui.',
    noChildrenYet: 'Aucun enfant pour l’instant.',
    noShoppingYet: 'Aucune liste de courses.',
    noExpensesYet: 'Aucune dépense ce mois-ci pour l’instant.',
    noNotesYet: 'Aucun mot pour l’instant.',
    thisMonth: 'Ce mois-ci',
    /** Expenses summary line, e.g. "3 nevyrovnané". */
    outstandingCount: '{count} en attente',
    /** Empty state — brand-new family with nothing added yet. */
    emptyTitle: 'Bienvenue à la maison 👋',
    emptyLead: 'Commencez en trois étapes et toute la famille est au même endroit.',
    emptyAddChild: 'Ajoutez votre premier enfant',
    emptyInvitePartner: 'Invitez votre partenaire',
    emptyCreateList: 'Créez une liste de courses',
  },

  children: {
    title: 'Enfants',
    add: 'Ajouter un enfant',
    addTitle: 'Ajouter un enfant',
    none: 'Aucun enfant pour l’instant.',
    noneLead: 'Ajoutez votre premier enfant — anniversaire, santé et contacts seront à portée de main.',
    nameLabel: 'Prénom',
    namePlaceholder: 'ex. Emma',
    emojiLabel: 'Avatar (emoji)',
    birthDateLabel: 'Date de naissance',
    ageYears: 'ans',
    selectPrompt: 'Sélectionnez un enfant ci-dessus.',
    /** Kid-profile destination: labels for the per-child quick links. */
    profileTitle: 'Profil de l’enfant',
    profileLead: 'Tout sur l’enfant au même endroit — en un clic.',
    quickHealth: 'Santé',
    quickContacts: 'Contacts',
    quickCalendar: 'Calendrier',
    quickDocuments: 'Documents',
    birthday: 'Anniversaire',
    ageLabel: 'Âge',
    noBirthDate: 'La date de naissance n’est pas renseignée.',
    editTitle: 'Modifier l’enfant',
    removeConfirm: 'Retirer vraiment cet enfant ? Ses données restent conservées.',
  },

  contacts: {
    title: 'Contacts',
    add: 'Ajouter un contact',
    empty: 'Aucun contact pour l’instant.',
    typeLabel: 'Type',
    labelLabel: 'Nom',
    labelPlaceholder: 'ex. Dre Martin',
    phoneLabel: 'Téléphone',
    addressLabel: 'Adresse',
    types: {
      doctor: 'Médecin',
      school: 'École',
      dentist: 'Dentiste',
      activity: 'Activité',
      other: 'Autre',
    },
  },

  health: {
    title: 'Dossier de santé',
    sensitive: 'Données sensibles (RGPD art. 9)',
    lead: 'Les informations de santé importantes au même endroit — en cas d’urgence.',
    allergies: 'Allergies',
    medications: 'Médicaments',
    insurance: 'Assurance maladie',
    bloodType: 'Groupe sanguin',
    conditions: 'Diagnostics / pathologies',
    notes: 'Notes',
    updatedAt: 'Dernière modification',
    placeholderNone: 'Non renseigné',
  },

  calendar: {
    title: 'Calendrier',
    add: 'Ajouter un événement',
    empty: 'Aucun événement pour l’instant.',
    upcoming: 'À venir',
    titleLabel: 'Titre de l’événement',
    typeLabel: 'Type',
    startLabel: 'Début',
    endLabel: 'Fin',
    allDayLabel: 'Toute la journée',
    locationLabel: 'Lieu',
    /** Separated-mode only: the custody sub-view tab label. */
    custodyView: 'Garde alternée',
    /**
     * Event-type labels. `types` is the family-mode default (no handover/custody
     * surfaced). `typesSeparated` adds the co-parenting types and is selected
     * when `family.mode === 'separated'`. Both carry every key so callers can
     * index either map by the same `CalendarEventType`.
     */
    types: {
      appointment: 'Rendez-vous',
      handover: 'Autre',
      custody: 'Autre',
      school: 'École',
      activity: 'Activité',
      other: 'Autre',
    },
    typesSeparated: {
      appointment: 'Rendez-vous',
      handover: 'Passation',
      custody: 'Alternance',
      school: 'École',
      activity: 'Activité',
      other: 'Autre',
    },
  },

  custody: {
    title: 'Planning de la garde alternée',
    none: 'Le planning de la garde alternée n’est pas encore défini.',
    patternLabel: 'Rythme',
    startLabel: 'Début du planning',
    notesLabel: 'Notes',
    set: 'Définir le planning',
    update: 'Modifier le planning',
    parentA: 'Parent A',
    parentB: 'Parent B',
    thisWeek: 'Cette semaine',
    patterns: {
      'week-week': 'Semaine / semaine',
      '2-2-3': '2-2-3',
      custom: 'Personnalisé',
    },
  },

  documents: {
    title: 'Documents',
    sensitive: 'Sensible',
    add: 'Ajouter un document',
    empty: 'Aucun document pour l’instant.',
    nameLabel: 'Nom',
    categoryLabel: 'Catégorie',
    fileLabel: 'Fichier',
    fileHint: 'En mode local, le fichier n’est pas téléversé — seules les métadonnées sont enregistrées.',
    uploadedBy: 'Ajouté par',
    size: 'Taille',
  },

  shopping: {
    title: 'Courses',
    /** List picker + creation. */
    listLabel: 'Liste',
    newList: 'Nouvelle liste',
    newListTitle: 'Nouvelle liste de courses',
    listNameLabel: 'Nom de la liste',
    listNamePlaceholder: 'ex. Épicerie',
    renameList: 'Renommer la liste',
    deleteList: 'Supprimer la liste',
    deleteListConfirm: 'Supprimer vraiment cette liste avec tous ses articles ?',
    noLists: 'Aucune liste pour l’instant.',
    noListsLead: 'Créez votre première liste — par exemple « Épicerie » ou « Droguerie ».',
    /** Item add + list. */
    addItem: 'Ajouter un article',
    addItemPlaceholder: 'Ajouter un article…',
    itemNameLabel: 'Article',
    empty: 'La liste est vide.',
    emptyLead: 'Ajoutez votre premier article ci-dessus.',
    /** Category grouping (free-form label on each item). */
    categoryLabel: 'Catégorie',
    categoryNone: 'Sans catégorie',
    categoryPlaceholder: 'ex. Fruits et légumes',
    /** Assignee = the member responsible for buying the item. */
    assigneeLabel: 'Qui achète',
    assigneeNone: 'N’importe qui',
    /** Buy-off. */
    bought: 'Acheté',
    markBought: 'Marquer comme acheté',
    clearBought: 'Effacer les achetés',
    /** {count} → number of checked items to clear. */
    clearBoughtCount: 'Effacer les achetés ({count})',
    clearBoughtConfirm: 'Effacer tous les articles achetés de cette liste ?',
    itemCount: '{count} articles',
  },

  messages: {
    /**
     * "Mots" in family mode (warm) / "Journal des messages" in separated
     * (court-log). Bare `title`/`lead`/`empty` default to the family wording;
     * the `*Family` and `*Separated` variants let the mode-aware UI pick
     * explicitly. Under the hood messages stay append-only in BOTH modes.
     */
    title: 'Mots',
    titleFamily: 'Mots',
    titleSeparated: 'Journal des messages',
    lead: 'De brefs mots et notes pour la famille — par exemple « Va chercher Emma à 15 h ».',
    leadFamily: 'De brefs mots et notes pour la famille — par exemple « Va chercher Emma à 15 h ».',
    leadSeparated:
      'Un registre inaltérable, recevable en justice, de la communication. Les messages ne peuvent être ni modifiés ni supprimés.',
    placeholder: 'Écrivez un mot…',
    placeholderSeparated: 'Écrivez un message…',
    send: 'Envoyer',
    empty: 'Aucun mot pour l’instant. Écrivez le premier.',
    emptyFamily: 'Aucun mot pour l’instant. Écrivez le premier.',
    emptySeparated: 'Le journal est encore vide. Écrivez le premier message.',
    newest: 'Le plus récent',
    /** Shown only in separated mode (the append-only guarantee matters there). */
    appendOnlyNote: 'En ajout seul — sans modification ni suppression.',
  },

  expenses: {
    /**
     * "Dépenses du foyer" (household budgeting) in family mode vs "Dépenses"
     * with split/settle machinery in separated. Bare keys default to family
     * wording; `*Separated` variants carry the co-parenting framing.
     */
    title: 'Dépenses du foyer',
    titleFamily: 'Dépenses du foyer',
    titleSeparated: 'Dépenses',
    lead: 'Une vue d’ensemble de qui a payé quoi — le budget commun du foyer.',
    leadSeparated: 'Les dépenses communes pour les enfants et leur répartition entre les parents.',
    add: 'Ajouter une dépense',
    empty: 'Aucune dépense pour l’instant.',
    amountLabel: 'Montant',
    categoryLabel: 'Catégorie',
    descriptionLabel: 'Description',
    childLabel: 'Enfant',
    childAll: 'Toute la famille',
    incurredOnLabel: 'Date',
    /** Split UI is hidden in family mode; these labels surface in separated. */
    splitLabel: 'Partage',
    split5050: 'Moitié-moitié (50/50)',
    splitCustom: 'Personnalisé',
    payerShare: 'Votre part',
    otherShare: 'Part de l’autre parent',
    /** Family mode shows only "kto zaplatil" without any split. */
    paidBy: 'Payé par',
    paidByFamily: 'Payé par',
    settled: 'Réglé',
    outstanding: 'En attente',
    settle: 'Marquer comme réglé',
    totalOutstanding: 'Total en attente',
    /** Family-mode month total (no settlement concept). */
    totalMonth: 'Total ce mois-ci',
    you: 'Vous',
  },

  agreements: {
    title: 'Accords',
    lead:
      'Des accords écrits entre les parents. Un parent propose un accord, l’autre l’approuve ou le refuse. Un accord approuvé constitue un registre inaltérable et recevable en justice.',
    propose: 'Proposer un accord',
    proposeTitle: 'Proposer un accord',
    proposeLead:
      'Formulez l’accord clairement. L’autre parent pourra l’approuver ou le refuser.',
    proposeNote:
      'Une fois approuvé, l’accord est verrouillé et ne peut plus être modifié ni supprimé.',
    titleLabel: 'Titre de l’accord',
    titlePlaceholder: 'ex. Vacances d’été 2026',
    bodyLabel: 'Texte de l’accord',
    bodyPlaceholder: 'Décrivez ce sur quoi vous vous êtes mis d’accord…',
    empty: 'Aucun accord pour l’instant.',
    emptyLead: 'Proposez votre premier accord à l’autre parent.',
    proposedBy: 'Proposé par',
    respondedBy: 'Traité par',
    accept: 'Approuver',
    decline: 'Refuser',
    withdraw: 'Retirer',
    locked: 'Verrouillé — aucune autre modification.',
    actionStale: 'Cet accord a entre-temps été traité. Nous avons actualisé la liste.',
    you: 'Vous',
    status: {
      proposed: 'Proposé',
      accepted: 'Approuvé',
      declined: 'Refusé',
      withdrawn: 'Retiré',
    },
  },

  sharing: {
    title: 'Famille',
    lead:
      'Découvrez qui fait partie de la famille et décidez ce que chacun voit. Vous pouvez inviter une mamie ou un papi comme personne de confiance — il ne voit que ce que vous autorisez.',
    familyNameLabel: 'Nom de la famille',
    rename: 'Renommer',
    renameTitle: 'Renommer la famille',
    members: 'Membres de la famille',
    invite: 'Inviter',
    inviteMember: 'Inviter un membre',
    inviteTitle: 'Inviter un membre de la famille',
    emailLabel: 'E-mail',
    roleLabel: 'Rôle dans la famille',
    roleParent: 'Parent',
    roleParentDesc: 'Accès complet à tout — pour l’autre parent.',
    roleTrusted: 'Personne de confiance',
    roleTrustedDesc:
      'Une mamie ou un papi, par exemple — ne voit que ce que vous cochez.',
    scopesLabel: 'Ce qu’elle peut voir',
    scopesHint:
      'Un parent voit tout. Une personne de confiance ne voit que les domaines cochés.',
    parentScopesNote:
      'Un parent obtient automatiquement un accès complet à tous les domaines.',
    sensitiveNote: 'Sensible — accordé uniquement de façon explicite.',
    seesLabel: 'Voit',
    seesNothing: 'Aucun accès à un domaine pour le moment.',
    fullAccess: 'Accès complet',
    status: {
      invited: 'Invité(e)',
      active: 'Actif(ve)',
    },
    you: 'Vous',
    parentBadge: 'Parent',
    trustedBadge: 'Personne de confiance',
    changeScopes: 'Modifier l’accès',
    removeMember: 'Retirer',
    removeConfirm: 'Retirer vraiment cette personne de la famille ?',
    onlyParents:
      'Seuls les parents peuvent gérer les membres de la famille et leur accès.',
  },

  scopes: {
    children: 'Enfants',
    calendar: 'Calendrier',
    contacts: 'Contacts',
    health: 'Santé',
    documents: 'Documents',
    messages: 'Mots',
    expenses: 'Dépenses',
    shopping: 'Courses',
  },

  mode: {
    /** Settings → "Režim rodiny" card. Parent-only, lives at the bottom. */
    cardTitle: 'Mode de la famille',
    cardLead:
      'Solandio est configuré pour une famille qui vit ensemble. Si vous vivez dans des foyers séparés, vous pouvez changer de mode — toutes vos données et tout l’historique des enfants restent intacts.',
    /** Current-mode labels. */
    familyLabel: 'Foyer commun',
    familyDesc: 'Calendrier, enfants, courses et dépenses pour toute la famille.',
    separatedLabel: 'Foyers séparés',
    separatedDesc:
      'Ajoute la garde alternée, le partage des dépenses, les accords et le journal des messages.',
    currentLabel: 'Mode actuel',
    /** Switch actions. */
    switchToSeparated: 'Passer aux foyers séparés',
    switchToFamily: 'Revenir au foyer commun',
    /** Two-step confirmation dialog (deliberate friction). */
    confirmToSeparatedTitle: 'Passer au mode foyers séparés ?',
    confirmToSeparatedLead:
      'Nous ajouterons des outils pour les parents qui vivent séparément — la garde alternée, le partage des dépenses, les accords et le journal des messages.',
    confirmToSeparatedNote:
      'Toutes vos données et tout l’historique des enfants restent intacts. Vous pouvez revenir en arrière à tout moment.',
    confirmToSeparatedAction: 'Oui, changer',
    confirmToFamilyTitle: 'Revenir au foyer commun ?',
    confirmToFamilyLead:
      'Les outils pour foyers séparés seront masqués. Les données restent enregistrées — au prochain changement, vous les retrouverez telles quelles.',
    confirmToFamilyAction: 'Oui, revenir',
    /** Shown while the change is being saved + after it lands. */
    switching: 'Changement de mode…',
    switched: 'Le mode a changé.',
    /** Gate copy when a non-parent somehow reaches the card. */
    onlyParents: 'Seuls les parents peuvent changer le mode de la famille.',
  },

  audit: {
    title: 'Journal',
    lead: 'Qui a modifié quoi et quand. Le registre est inaltérable (ajout seul).',
    empty: 'Aucune entrée pour l’instant.',
    onlyParents: 'Seuls les parents peuvent consulter le journal.',
  },

  errors: {
    forbidden: 'Vous n’avez pas l’autorisation d’accéder à ce domaine.',
    notFound: 'Élément introuvable.',
    generic: 'L’action a échoué. Veuillez réessayer.',
  },

  tour: {
    /** Re-invoke action (Settings + help button in the top bar). */
    start: 'Lancer la visite',
    help: 'Visite',
    helpHint: 'Revoyez la présentation rapide de l’application à tout moment.',
    /** Joyride control labels. */
    back: 'Retour',
    next: 'Suivant',
    last: 'Terminé',
    skip: 'Passer',
    close: 'Fermer',
    /** "Krok 2 z 6" — {step} a {steps} doplní Joyride. */
    progress: 'Étape {step} sur {steps}',
    steps: {
      welcomeTitle: 'Bienvenue sur Solandio',
      welcome: 'On vous montre tout en quelques secondes.',
      childrenTitle: 'Enfants',
      children: 'Ajoutez et changez d’enfant ici.',
      modulesTitle: 'Tout sur la famille',
      modules:
        'À gauche, vous trouverez tout pour la famille — calendrier, enfants, santé, courses, dépenses, documents et mots.',
      familyTitle: 'Famille',
      family:
        'Invitez ici votre partenaire ou une mamie/un papi et définissez ce qu’ils voient.',
      settingsTitle: 'Paramètres',
      settings: 'Dans les Paramètres, vous changez votre nom et la langue.',
      finishTitle: 'C’est prêt !',
      finish: 'Commencez par ajouter un enfant ou une liste de courses.',
    },
  },
} as const;
